import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import { useAuth } from './auth';

/**
 * Les pastilles du menu.
 *
 * Le logiciel ne doit pas attendre qu'on aille voir : c'est l'assurance qui
 * expire dans huit jours qui vient vous chercher. Le compte est rafraichi
 * toutes les cinq minutes et apres chaque changement d'ecran — assez pour que
 * ce soit vivant, assez rare pour ne rien couter sur le petit serveur.
 */
export interface Alerte { nombre: number; niveau: 'danger' | 'attention'; detail: string }
type Table = Record<string, Alerte>;

const Ctx = createContext<{ alertes: Table; recharger: () => void }>({ alertes: {}, recharger: () => {} });

const INTERVALLE_MS = 5 * 60 * 1000;

export function AlertesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [alertes, setAlertes] = useState<Table>({});

  const recharger = useCallback(() => {
    if (!user) { setAlertes({}); return; }
    // Un echec est sans consequence : on n'affiche simplement aucune pastille.
    api.get<Table>('/taches/alertes').then(setAlertes).catch(() => {});
  }, [user]);

  useEffect(() => {
    recharger();
    if (!user) return;
    const t = setInterval(recharger, INTERVALLE_MS);
    return () => clearInterval(t);
  }, [recharger, user]);

  const valeur = useMemo(() => ({ alertes, recharger }), [alertes, recharger]);
  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export const useAlertes = () => useContext(Ctx);

/** La pastille elle-meme : un compte, une couleur, et l'explication au survol. */
export function Pastille({ a }: { a: Alerte | undefined }) {
  if (!a || a.nombre === 0) return null;
  return (
    <span className={`pastille ${a.niveau}`} title={a.detail} aria-label={a.detail}>
      {a.nombre > 99 ? '99+' : a.nombre}
    </span>
  );
}
